import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef, useRef } from "react";
import "../Assets/Css/Table.css";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";

/**
 * Server-driven table: pagination (pageNo, rowCount) and search (keyword) via fetchPage.
 * Parent implements fetchPage: async ({ pageNo, rowCount, keyword }) => ({ result, totalCount })
 */
const GlobalDataTable = forwardRef(function GlobalDataTable(
  {
    fetchPage,
    defaultRowCount = 10,
    rowCountOptions = [10, 20, 50, 100],
    searchPlaceholder = "Search...",
    showGridlines = true,
    emptyMessage = "No records found.",
    headerTitle,
    headerExtras,
    toolbarExtras,
    children,
    dataKey = "uuid",
    onRowsChange,
    onTableStateChange,
    initialFirst = 0,
    initialRows,
    initialSearch = "",
    tableStyle = { minWidth: "700px" },
    ...rest
  },
  ref
) {
  const resolvedInitialRows = initialRows ?? defaultRowCount;
  const resolvedInitialSearch = initialSearch == null ? "" : String(initialSearch);
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(resolvedInitialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(resolvedInitialSearch.trim());
  const [lazyState, setLazyState] = useState({
    first: Math.max(0, Number(initialFirst) || 0),
    rows: resolvedInitialRows,
  });

  const fetchPageRef = useRef(fetchPage);
  fetchPageRef.current = fetchPage;
  const onRowsChangeRef = useRef(onRowsChange);
  onRowsChangeRef.current = onRowsChange;
  const onTableStateChangeRef = useRef(onTableStateChange);
  onTableStateChangeRef.current = onTableStateChange;

  const prevDebouncedRef = useRef(resolvedInitialSearch.trim());

  useEffect(() => {
    const id = setTimeout(() => {
      const next = searchInput.trim();
      if (prevDebouncedRef.current !== next) {
        prevDebouncedRef.current = next;
        setDebouncedSearch(next);
        setLazyState((s) => ({ ...s, first: 0 }));
      }
    }, 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  const load = useCallback(async () => {
    const pageNo = Math.floor(lazyState.first / lazyState.rows) + 1;
    setLoading(true);
    try {
      const { result, totalCount } = await fetchPageRef.current({
        pageNo,
        rowCount: lazyState.rows,
        keyword: debouncedSearch || undefined,
      });
      const list = Array.isArray(result) ? result : [];
      setRows(list);
      setTotalRecords(Number(totalCount) || 0);
      onRowsChangeRef.current?.(list);
    } catch {
      setRows([]);
      setTotalRecords(0);
      onRowsChangeRef.current?.([]);
    } finally {
      setLoading(false);
    }
  }, [lazyState.first, lazyState.rows, debouncedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    onTableStateChangeRef.current?.({
      first: lazyState.first,
      rows: lazyState.rows,
      keyword: debouncedSearch,
      pageNo: Math.floor(lazyState.first / lazyState.rows) + 1,
    });
  }, [lazyState.first, lazyState.rows, debouncedSearch]);

  useImperativeHandle(
    ref,
    () => ({
      reload: () => load(),
      getState: () => ({
        first: lazyState.first,
        rows: lazyState.rows,
        keyword: debouncedSearch,
        pageNo: Math.floor(lazyState.first / lazyState.rows) + 1,
      }),
      setSearch: (value) => {
        const v = value == null ? "" : String(value);
        setSearchInput(v);
        const t = v.trim();
        prevDebouncedRef.current = t;
        setDebouncedSearch(t);
        setLazyState((s) => ({ ...s, first: 0 }));
      },
    }),
    [load, lazyState.first, lazyState.rows, debouncedSearch]
  );

  const onPage = (e) => {
    setLazyState({ first: e.first, rows: e.rows });
  };

  const renderHeader = () => (
    <div className="header-container">
      {(headerTitle || headerExtras) && (
        <div className="header-row">
          {headerTitle && (
            <div className="header-title">
              <h5 className="m-0">{headerTitle}</h5>
            </div>
          )}
          {headerExtras}
        </div>
      )}
      <div className="search-row">
        <div className="search-section">
          <div className="search-item">
            <label className="search-label">
              <i className="fa fa-search me-1"></i>
              Search:
            </label>
            <IconField iconPosition="left" className="search-input-container w-100">
              <i className="fa fa-search search-icon"></i>
              <InputText
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={searchPlaceholder}
                className="search-input"
              />
            </IconField>
          </div>
          {toolbarExtras}
        </div>
      </div>
    </div>
  );

  return (
    <DataTable
      value={rows}
      lazy
      paginator
      first={lazyState.first}
      rows={lazyState.rows}
      totalRecords={totalRecords}
      onPage={onPage}
      rowsPerPageOptions={rowCountOptions}
      showGridlines={showGridlines}
      tableStyle={tableStyle}
      loading={loading}
      dataKey={dataKey}
      header={renderHeader()}
      emptyMessage={emptyMessage}
      {...rest}
    >
      {children}
    </DataTable>
  );
});

export default GlobalDataTable;
